import React, { Suspense, lazy } from "react";
import { Navigate, useLocation, useRoutes } from "react-router-dom";
import useAuthContext from "../modules/auth/hook/useAuthContext";
import PaginaLanding from "../paginas/landing/PaginaLanding";
import PaginaLayout from "../paginas/layouts/PaginaLayout";
import PaginaLoading from "../paginas/utils/PaginaLoading";

interface LoadableProps {
  [key: string]: unknown;
}

const Loadable = (Component: React.ComponentType) => {
  return function LoadableComponent(props: LoadableProps) {
    return (
      <Suspense fallback={<PaginaLoading />}>
        <Component {...props} />
      </Suspense>
    );
  };
};

export default function Router() {
  const { user, searchingUser, navigation } = useAuthContext();
  const location = useLocation();
  const isAuthenticated = Boolean(user);

  const getAuthenticatedPath = (): string => {
    const firstModule = navigation[0];

    if (!firstModule) {
      return "/401";
    }

    return firstModule.vistas[0]?.pathRelativo || firstModule.pathRelativo || "/401";
  };

  const authenticatedPath = getAuthenticatedPath();
  const routes = useRoutes([
    { path: "/", element: isAuthenticated ? <Navigate to={authenticatedPath} replace /> : <PaginaLanding />},
    { path: "/auth",
       children: [
        { path: "ingreso", element: isAuthenticated ? <Navigate to={authenticatedPath} replace /> : <Login />},
      ],
    },
    { path: "/app", element: isAuthenticated ? ( <PaginaLayout /> ) : ( <Navigate to="/auth/ingreso" replace state={{ from: location.pathname }} /> ),
      children: [
        { index: true, element: <Navigate to="pacientes" replace />},
        { path: "dashboard", element: <Navigate to="/app/pacientes" replace />},
        { path: "pacientes", element: <PaginaPacientes />},
        { path: "pacientes/opciones", element: <PaginaPacienteVista />},        
        { path: "pacientes/opciones/perfil", element: <PaginaPacientePerfil />}, 
        { path: "pacientes/opciones/perfil/editar", element: <PaginaPacienteEditar />},
        { path: "pacientes/opciones/psicotests", element: <PaginaPsicotestsPaciente />},
        { path: "pacientes/opciones/psicotests/battelle", element: <BattelleTestPage />},
        { path: "pacientes/opciones/psicotests/adir", element: <ADIRPage />},
        { path: "pendings", element: <PaginaTareas />},
        { path: "ia", element: <PaginaIA />},
        { path: "ia/nuevo", element: <PaginaCrearInformeIA />},
        { path: "ia/:informeId/tests", element: <PaginaCrearInformeIA />},
      ],
    },
    { path: "/401", element: <NotAuthorizedPage />},
    { path: "/404", element: <NotFoundPage />},
    { path: "*", element: <Navigate to="/404" replace />},
  ]);

  if (searchingUser) {
    return <PaginaLoading />;
  }

  return routes;
}

// Authentication
const Login = Loadable(lazy(() => import("../paginas/autenticacion/PaginaLogin")));

// Pacientes
const PaginaPacientes = Loadable(lazy(() => import("../paginas/pacientes/PaginaPacientes")));
const PaginaPacienteVista = Loadable(lazy(() => import("../paginas/pacientes/PaginaPacienteVista")));
const PaginaPacientePerfil = Loadable(lazy(() => import("../paginas/pacientes/PaginaPacientePerfil")));
const PaginaPacienteEditar = Loadable(lazy(() => import("../paginas/pacientes/PaginaPacienteEditar")));
const PaginaTareas = Loadable(lazy(() => import("../paginas/tareas/PaginaTareas")));
const PaginaPsicotestsPaciente = Loadable(lazy(() => import("../paginas/psicotest/PaginaPsicotestsPaciente")));
const BattelleTestPage = Loadable(lazy(() => import("../paginas/psicotest/battelle/BattelleTestPage")));
const ADIRPage = Loadable(lazy(() => import("../paginas/psicotest/adir/ADIRPage")));

// IA
const PaginaIA = Loadable(lazy(() => import("../paginas/ia/PaginaIA")));
const PaginaCrearInformeIA = Loadable(lazy(() => import("../paginas/ia/PaginaCrearInformeIA")));

// 404
const NotFoundPage = Loadable(lazy(() => import("../paginas/404/Pagina404")));

// 401
const NotAuthorizedPage = Loadable(lazy(() => import("../paginas/401/Pagina401")));